import { useEffect, useState } from "react";
import { format } from "date-fns";
import { supabase } from "../lib/supabaseClient";
import { useTripRealtimeSync } from "../lib/useTripRealtimeSync";
import { EditTodoModal } from "./EditTodoModal";
import { useConfirmDialog } from "../contexts/ConfirmDialogContext";

// Typdefinitionen

type Todo = {
  id: string;
  trip_id: string;
  title: string;
  description: string | null;
  phase: string;
  due_date: string | null;
  required: boolean | null;
};

type Assignment = {
  id: string;
  todo_id: string;
  user_id: string;
  status: string | null;
};

type Props = {
  tripId: string;
  viewerId: string;
  isOrganizer: boolean;
};

let triggerExternalRefetch: () => void;

export function triggerRefetch() {
  triggerExternalRefetch?.();
}

export default function TodoList({ tripId, viewerId, isOrganizer }: Props) {
  const [todos, setTodos] = useState<Todo[]>([]);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [phaseFilter, setPhaseFilter] = useState<
    "all" | "before" | "during" | "after"
  >("all");
  const [refetchTrigger, setRefetchTrigger] = useState(0);
  const [editTodo, setEditTodo] = useState<Todo | null>(null);
  const { showConfirm } = useConfirmDialog();

  triggerExternalRefetch = () => setRefetchTrigger((prev) => prev + 1);

  // Realtime Updates
  useTripRealtimeSync<Todo>({
    tripId,
    table: "trip_todos",
    getId: (item) => item.id,
    setItems: setTodos,
  });

  useTripRealtimeSync<Assignment>({
    tripId,
    table: "trip_todo_assignments",
    getId: (item) => item.id,
    setItems: setAssignments,
  });

  const fetchTodos = async () => {
    const { data: todosData, error: todosError } = await supabase
      .from("trip_todos")
      .select("*")
      .eq("trip_id", tripId);

    if (todosError) {
      console.error("❌ Fehler beim Laden der Aufgaben:", todosError.message);
      return;
    }

    const todoIds = todosData?.map((t) => t.id) ?? [];

    const { data: assignmentsData, error: assignmentsError } = await supabase
      .from("trip_todo_assignments")
      .select("*")
      .in("todo_id", todoIds);

    if (assignmentsError) {
      console.error(
        "❌ Fehler beim Laden der Zuweisungen:",
        assignmentsError.message
      );
      return;
    }

    setTodos(todosData ?? []);
    setAssignments(assignmentsData ?? []);
  };

  useEffect(() => {
    if (tripId) fetchTodos();
  }, [tripId, refetchTrigger]);

  const toggleDone = async (assignment: Assignment) => {
    const newStatus = assignment.status === "done" ? "pending" : "done";

    const { error } = await supabase
      .from("trip_todo_assignments")
      .update({ status: newStatus })
      .eq("id", assignment.id);

    if (error) {
      console.error("❌ Fehler beim Aktualisieren der Aufgabe:", error.message);
    }
  };

  const deleteTodo = async (id: string) => {
    const { error } = await supabase.from("trip_todos").delete().eq("id", id);
    if (error) {
      console.error("❌ Fehler beim Löschen:", error.message);
    } else {
      triggerRefetch();
    }
  };

  const filteredTodos = todos.filter((t) => {
    if (phaseFilter !== "all" && t.phase !== phaseFilter) return false;
    if (isOrganizer) return true;

    const isAssigned = assignments.some(
      (a) => a.todo_id === t.id && a.user_id === viewerId
    );

    return isAssigned;
  });

  const sortedTodos = [...filteredTodos].sort((a, b) => {
    const aAssignment = assignments.find(
      (as) => as.todo_id === a.id && as.user_id === viewerId
    );
    const bAssignment = assignments.find(
      (as) => as.todo_id === b.id && as.user_id === viewerId
    );

    const aDone = aAssignment?.status === "done";
    const bDone = bAssignment?.status === "done";

    if (a.required !== b.required) return a.required ? -1 : 1;
    if (aDone !== bDone) return aDone ? 1 : -1;
    if (a.due_date && b.due_date) {
      return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
    } else if (a.due_date) {
      return -1;
    } else if (b.due_date) {
      return 1;
    }
    return a.title.localeCompare(b.title);
  });

  /** Unified filter chip */
  const FilterChip = ({
    label,
    active,
    onClick,
  }: {
    label: string;
    active: boolean;
    onClick: () => void;
  }) => (
    <button
      onClick={onClick}
      className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium transition-colors ${
        active
          ? "bg-sky-500/20 text-sky-400 border border-sky-500/30"
          : "bg-[#1e2639] text-slate-400 hover:text-slate-200"
      }`}
    >
      {label}
      {active && <span className="opacity-50 ml-0.5">✕</span>}
    </button>
  );

  return (
    <div className="mt-6">
      <h3 className="text-xs uppercase tracking-widest text-slate-500 font-medium mb-5 px-1">
        Aufgaben
      </h3>

      {/* Filter chips */}
      <div className="flex flex-wrap gap-2 mb-5">
        {[
          ["before", "⏰ Vor der Reise"],
          ["during", "🌞 Während"],
          ["after", "📦 Danach"],
        ].map(([key, label]) => (
          <FilterChip
            key={key}
            label={label}
            active={phaseFilter === key}
            onClick={() =>
              setPhaseFilter((prev) => (prev === key ? "all" : (key as any)))
            }
          />
        ))}
        {phaseFilter !== "all" && (
          <button
            onClick={() => setPhaseFilter("all")}
            className="ml-auto text-[11px] text-slate-600 hover:text-slate-400 transition-colors"
          >
            Zurücksetzen
          </button>
        )}
      </div>

      {sortedTodos.length === 0 ? (
        isOrganizer ? (
          <div className="text-center py-10">
            <p className="text-slate-500 text-sm">Noch keine Aufgaben definiert.</p>
            <p className="text-slate-700 text-xs mt-1">
              Verteile Aufgaben – so bleibt die Reise reibungslos organisiert ✅
            </p>
          </div>
        ) : (
          <div className="text-center py-10">
            <p className="text-slate-500 text-sm">Noch keine Aufgaben für dich.</p>
          </div>
        )
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {sortedTodos.map((todo) => {
            const ownAssignment = assignments.find(
              (a) => a.todo_id === todo.id && a.user_id === viewerId
            );

            const isDone = ownAssignment?.status === "done";

            return (
              <div
                key={todo.id}
                className="bg-[#161b27] rounded-xl border border-[#2a3347] p-4 hover:border-sky-500/20 transition-colors flex flex-col gap-2"
              >
                {/* Title + badge */}
                <div className="flex items-start justify-between gap-2">
                  <h4
                    className={`text-sm font-semibold leading-snug flex-1 ${
                      isDone ? "text-slate-500 line-through" : "text-slate-100"
                    }`}
                  >
                    {todo.title}
                  </h4>
                  {todo.required && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-500 shrink-0">
                      Pflicht
                    </span>
                  )}
                </div>

                {/* Description */}
                {todo.description && (
                  <p className="text-xs text-slate-400 leading-relaxed">
                    {todo.description}
                  </p>
                )}

                {/* Due date */}
                {todo.due_date && (
                  <div className="text-[10px] text-slate-600">
                    Frist: {format(new Date(todo.due_date), "dd.MM.yyyy")}
                  </div>
                )}

                {/* Own assignment checkbox */}
                {ownAssignment && (
                  <div className="flex items-center gap-2 mt-1 pt-2 border-t border-[#1e2639]">
                    <input
                      type="checkbox"
                      checked={isDone}
                      onChange={() => toggleDone(ownAssignment)}
                      className="w-3.5 h-3.5 accent-sky-500 cursor-pointer"
                    />
                    <span
                      className={`text-xs ${
                        isDone ? "text-emerald-400" : "text-slate-400"
                      }`}
                    >
                      {isDone ? "Erledigt" : "Noch offen"}
                    </span>
                  </div>
                )}

                {/* Organizer actions */}
                {isOrganizer && (
                  <div className="flex justify-end gap-3 mt-1">
                    <button
                      onClick={() => setEditTodo(todo)}
                      className="text-[11px] text-slate-600 hover:text-slate-300 transition-colors"
                    >
                      Bearbeiten
                    </button>
                    <button
                      onClick={async () => {
                        const confirmed = await showConfirm({
                          title: "Aufgabe löschen",
                          message:
                            "❌ Diese Aufgabe wirklich löschen? Die Aufgabe wird für alle gelöscht und ist dann nicht mehr sichtbar. Wenn du für Dich diese Aufgabe erledigt hast, dann solltest du diese nicht löschen sondern nur abhaken. Diese Aktion kann nicht rückgängig gemacht werden.",
                          confirmLabel: "Löschen",
                          cancelLabel: "Abbrechen",
                          icon: "warning",
                        });

                        if (confirmed) {
                          deleteTodo(todo.id);
                        }
                      }}
                      className="text-[11px] text-slate-600 hover:text-red-400 transition-colors"
                    >
                      Löschen
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {editTodo && (
        <EditTodoModal
          todo={editTodo}
          tripId={tripId}
          onClose={() => {
            setEditTodo(null);
          }}
          onUpdated={async () => {
            triggerRefetch(); // ✅ ist jetzt ein async Callback
          }}
        />
      )}
    </div>
  );
}
